import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import MainLayout from '$layout/MainLayout';
import TopSection from '$components/pages/carPark/TopSection';
import ContentSection from '$components/pages/carPark/Content';
import Methodology from '$components/pages/carPark/Methodology';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { WithResponsiveGlobalStyles } from '$util/theme/withResponsive';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'car-park';

const DynamicParkingCosts = dynamic(() => import('$components/pages/carPark/ParkingCosts'));

const getTitle = (pathname) => {
  switch (pathname) {
    case 'car-park-index-2020-eur':
      return 'EUR';
    case 'car-park-index-2020-usd':
      return 'USD';
    default:
      return 'GBP';
  }
};

const getPageMeta = (pathname) => ({
  title: `Parking Price Index ${getTitle(pathname)} | Fixter`,
  description: [
    'How the cost of parking in some cities vary, both in',
    'the UK and around the world? The 2020 Parking Price',
    'Index shows the cost of parking in 65 cities and',
    'paints a very interesting picture.',
  ].join(' '),
  canonical: '',
  robots: true,
});

function CarParkPage(props) {
  const { pathname } = props;

  return (
    <MainLayout
      className="car-park"
      pageMeta={getPageMeta(pathname)}
      seo={{ noindex: false, nofollow: false }}
    >
      <WithResponsiveGlobalStyles />
      <TopSection {...props} />
      <ContentSection {...props} />
      <DynamicParkingCosts {...props} />
      <Methodology />
    </MainLayout>
  );
}

export const getServerSideProps = async function ({ req, res, query: urlQuery, preview: isPreviewMode }) {
  const pathname = req.url.slice(1);
  const locale = getLocaleFromHostname(req.headers.host || '');

  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  /**
   * Disallow direct access to template.
   * @see next.config.js rewrites
   */
  if (pathname === 'car-park') {
    return {
      redirect: {
        statusCode: 301,
        destination: '/car-park-index-2020-gbp',
      },
    };
  }

  const serverParams = {
    pathname,
    pageType: [],
    pageTypeFromPath: pathname,
  };

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      getNavbarQuery,
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return {
    props: { serverParams, pathname, initChat: false, cmsContent },
  };
};

CarParkPage.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default CarParkPage;
